<template>
    <centered-content-layout>
        <Loader v-if="showLoader" :status="showLoader"></Loader>
        <div class="small-content-block">
            <div class="page-title font-3">
                <span class="title-step-number">Step 2</span> Activate ad units and integrate the SDK
            </div>
        </div>
        <div class="main-content-block">
            <div class="content left-content">
                <app-header v-if="isAppDataResolved"
                            :platform="appData.platform"
                            :appKey="appData.appKey"
                            :appName="appData.name"
                            :appIcon="appData.icon"
                ></app-header>
                <div class="info font-5">Select the ad units you want to go live.<br/>
                    You can always change them later.</div>
            </div>
            <div class="content right-content">
                <div v-for="adUnit in adUnitsSwitcherDataArray">
                    <div class="gray-block">
                        <div class="block-content">
                            <div class="circle">
                                <IconBase height="24"
                                          width="14"
                                          :iconName="adUnit.name + 'Icon'"
                                          :iconColor="getAdUnitColor()" />
                            </div>
                            <div :class="'adunit-title-block' + adUnit.id"
                                 class="adunit-title-block font-4">{{adUnit.displayName}}</div>
                            <Tooltip v-if="adUnit.id === 'OW'" :text="owTooltipText">
                                <Switcher
                                    class="switcherMiddleAlign"
                                    :uniquIid="adUnit.name + 'SwitcherContainer'"
                                    :isDisabled="true"
                                    :isActive=adUnit.status
                                    :styles="{look: 'adunitLiveSwitcher'}"
                                    @changed="selectAdunitSwitcher(adUnit.id)">
                                </Switcher>
                            </Tooltip>
                            <switcher v-else :uniqueId="adUnit.name + 'SwitcherContainer'"
                                      :isActive=adUnit.status
                                      :styles="{look: 'adunitLiveSwitcher'}"
                                      :isDisabled="false"
                                      class="switcherMiddleAlign"
                                      @changed="selectAdunitSwitcher(adUnit.id)"/>
                        </div>
                    </div>
                </div>

            </div>
        </div>
        <div class="small-content-block footer">
            <div class="footer-link font-4">
                <span>To go live, integrate the
                    <GeneralLink class="link"
                                 :path="downloadSDKLink.url"
                                 :openInNewTab="true"
                                 :isExternal="true">
                    {{ downloadSDKLink.linkText }}
                    </GeneralLink>
                </span>
            </div>
            <div class="app-wizard-save-button submit">
                <div @click="saveAdUnits" class="iron-button font-4">Continue</div>
            </div>
        </div>
    </centered-content-layout>
</template>

<script>
import CenteredContentLayout from '@/pages/applications/components/CenteredContentLayout.vue';
import AppHeader from 'components/appHeader/AppHeader.vue';
import Loader from 'components/loader/loader.vue';
import IconBase from 'components/IconBase.vue';
import Tooltip from 'components/Tooltip/Tooltip.vue';
import consts from 'services/metaData/consts';
import Switcher from 'components/Switcher.vue';
import monetizeAppService from 'services/monetizeApp';
import GeneralLink from 'components/layout/GeneralLink.vue';
import authService from 'services/auth';
import links from 'services/metaData/links';
// eslint-disable-next-line import/no-cycle
import { navigateToAngularState } from 'services/utils/angularRouterUtils';
import { mapMutations } from 'vuex';

export default {
    name: 'AddNewAppAdUnits',
    components: {
        Switcher, Loader, Tooltip, GeneralLink, IconBase, AppHeader, CenteredContentLayout
    },
    async beforeMount() {
        this.setPage({
            pageTitle: 'Add New App'
        });
        const data = await monetizeAppService.getMonetizeApplicationByAppKey(this.appKey);
        this.formatAndSetAppData(data);
        this.isAppDataResolved = true;
    },
    data() {
        return {
            isAppDataResolved: false,
            owTooltipText: 'To activate this ad unit, your account need to be approved',
            appDataResolved: {},
            showLoader: false,
            downloadSDKLink: {
                url: links.downloadSDKUrl,
                linkText: 'ironSource SDK'
            },
            adUnitsSwitcherDataArray: [
                {
                    id: 'RV',
                    name: 'rewardedVideo',
                    displayName: 'RewardedVideo',
                    status: false
                },
                {
                    id: 'IS',
                    name: 'interstitial',
                    displayName: 'Interstitial',
                    status: false
                },
                {
                    id: 'OW',
                    name: 'offerWall',
                    displayName: 'OfferWall',
                    status: false
                },
                {
                    id: 'BN',
                    name: 'banner',
                    displayName: 'Banner',
                    status: false
                }
            ],
            updateAdUnitsDataObject: {
                adUnits: {
                    rewardedVideo: {
                        status: ''
                    },
                    interstitial: {
                        status: ''
                    },
                    offerWall: {
                        status: ''
                    },
                    banner: {
                        status: ''
                    }
                }
            }
        };
    },
    props: {
        appKey: {
            type: String
        }
    },
    methods: {
        ...mapMutations({
            setPage: 'setPage'
        }),
        getAdUnitColor() {
            return consts.COLORS.LIGHT_700;
        },
        formatAndSetAppData(data) {
            if (!(data && data.platform && data.icon && data.name && data.appKey)) {
                return;
            }
            this.appDataResolved.platform = data.platform.toLowerCase();
            this.appDataResolved.icon = data.icon;
            this.appDataResolved.name = data.name;
            this.appDataResolved.appKey = data.appKey;
        },
        getStatus(val) {
            return val ? 'active' : 'inactive';
        },
        prepareAdunitDataToSend() {
            for (let i = 0; i < this.adUnitsSwitcherDataArray.length; i++) {
                const { name, status } = this.adUnitsSwitcherDataArray[i];
                this.updateAdUnitsDataObject.adUnits[name].status = this.getStatus(status);
            }
        },
        handleAdunitsSaveFailed() {
            this.showLoader = false;
        },
        async saveAdUnits() {
            this.prepareAdunitDataToSend();
            if (await authService.isAuthorized(['monetizerTester'])) {
                await this.continueOnBoardingFlow();
                return;
            }
            this.showLoader = true;
            await monetizeAppService.changeAdUnitStatus(this.appKey, this.updateAdUnitsDataObject).then(() => {
                this.continueOnBoardingFlow();
            }, this.handleAdunitsSaveFailed);
        },
        async continueOnBoardingFlow() {
            await navigateToAngularState('adSettings.container', { app: this.appKey });
        },
        selectAdunitSwitcher(currentSwitcher) {
            if (currentSwitcher === 'OW') {
                return;
            }
            for (let i = 0; i < this.adUnitsSwitcherDataArray.length; i++) {
                const { id } = this.adUnitsSwitcherDataArray[i];
                if (id === currentSwitcher) {
                    this.adUnitsSwitcherDataArray[i].status = !this.adUnitsSwitcherDataArray[i].status;
                }
            }
        }
    },
    computed: {
        appData() {
            return this.appDataResolved;
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide";

$lightGray: #678099;
$gray-blockBg: #f3f5f7;
$textColor: #172d4c;
$mediumGray: #678099;
$leftMargin: 60px;
$rightMargin: 62px;

div {
    color: $textColor;
}

.link{
    color: $primary;
    cursor: pointer;
    text-decoration: none;
}

.small-content-block{
    height: 73px;
    width: 100%;
    border-bottom: $border;
    .page-title {
        font-size: 15px;
        padding-left: 33px;
        padding-top: 28px;
        font-size: 15px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: normal;
        .title-step-number {
            color: $lightGray;
        }
    }
    &.footer {
        height: 80px;
        border-bottom: initial;
        border-top: $border;
        flex-wrap: wrap;
        justify-content: space-between;
        position: relative;
         .footer-link {
             display: inline-flex;
             margin-left: $leftMargin;
             padding-top: 31px;
             font-stretch: normal;
             font-style: normal;
             line-height: normal;
             letter-spacing: normal;
         }
        .app-wizard-save-button {
            position: absolute;
            bottom: 20px;
            right: $rightMargin;
            cursor: pointer;
        }
    }
}
.main-content-block {
    height: 393px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    .content {
        margin-top: 33px;
        &.left-content {
            margin-left: $leftMargin;
            .info {
                color: $mediumGray;
                margin-top: 20px;
                margin-left: 10px;
                line-height: 1.54;
            }
        }
        &.right-content {
            margin-right: $rightMargin;
        }
        .gray-block{
            width: 318px;
            height: 72px;
            background-color: $gray-blockBg;
            padding: 11px 21px 11px 13px;
            border-radius: 9px;
            margin-bottom: 8px;
            .adunit-title-block {
                margin: 20px 97px 16px 13px;

                &.adunit-title-blockRV {
                    margin-right: 67px;
                }

                &.adunit-title-blockBN {
                    margin-right: 112px;
                }
            }
            .switcherMiddleAlign {
                margin: 12px 0 10px 0px;
            }
            .circle {
                width: 50px;
                height: 50px;
                background-color: $white;
                padding: 13px 18px;
                border-radius: 25px;
            }
            .block-content {
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
            }
        }
    }
}

.iron-button {
    width: 140px;
    height: 40px;
    padding: 13px 40px 11px 42px;
    border-radius: 3px;
    background-color: $primary;
    color: white;
    border: initial;
}
</style>
